import { useLayoutEffect, useRef, useState } from "react";
import "./animated-number.css";

export function AnimatedNumber({ value, className = "", increaseIsGood = true }: {
  value: number;
  className?: string;
  increaseIsGood?: boolean;
}) {
  const [displayed, setDisplayed] = useState(value);
  const [cue, setCue] = useState<{ direction: "up" | "down"; good: boolean; id: number } | null>(null);
  const current = useRef(value);
  const revision = useRef(0);
  const width = useRef(Math.max(2, String(value).length));
  width.current = Math.max(width.current, String(value).length);

  useLayoutEffect(() => {
    const from = current.current;
    if (from === value) {
      setCue(null);
      return;
    }
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    const finish = () => {
      cancelAnimationFrame(frame);
      current.current = value;
      setDisplayed(value);
      setCue(null);
    };
    if (motion.matches) {
      finish();
      return;
    }

    const direction = value > from ? "up" : "down";
    setCue({ direction, good: (direction === "up") === increaseIsGood, id: ++revision.current });
    const started = performance.now();
    const duration = 420;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - started) / duration);
      if (progress === 1) {
        finish();
        return;
      }
      const eased = 1 - (1 - progress) ** 3;
      const next = Math.round(from + (value - from) * eased);
      if (next !== current.current) {
        current.current = next;
        setDisplayed(next);
      }
      frame = requestAnimationFrame(tick);
    };
    const onMotionChange = () => { if (motion.matches) finish(); };
    motion.addEventListener("change", onMotionChange);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      motion.removeEventListener("change", onMotionChange);
    };
  }, [value, increaseIsGood]);

  return (
    <output className={`animated-number handwritten-number${className ? ` ${className}` : ""}`} aria-label={String(value)} aria-live="off" style={{ minWidth: `${width.current}ch` }}>
      <span
        key={cue?.id ?? "settled"}
        className={`animated-number-display${cue ? ` is-${cue.direction} is-${cue.good ? "good" : "bad"}` : ""}`}
        aria-hidden="true"
      >{displayed}</span>
    </output>
  );
}
